// Parity P0 — "Komputer saya" on /settings: the computers the owner connected with `bystep serve`.
// Plain words only (a non-technical owner reads this); the install guide lives on /serve.
import { Link } from '@tanstack/react-router'
import { Laptop, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import { ApiError } from '#/lib/api'
import { fmtDate } from '#/lib/format'
import { m } from '#/paraglide/messages'
import { useDeleteMachine, useMachines, type MachineDto } from './machines-api'

const agentName = (a: string) => (a === 'claude' ? 'Claude Code' : a === 'codex' ? 'Codex' : a === 'cursor' ? 'Cursor' : a)

export function MachineStatus({ machine, compact }: { machine: MachineDto; compact?: boolean }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Badge variant={machine.online ? 'default' : 'outline'} className={machine.online ? 'bg-status-done text-white' : 'text-muted-foreground'}>
        {machine.online ? m.serve_online() : m.serve_offline()}
      </Badge>
      {!compact && (
        <Badge variant="outline" className={machine.tunnel ? 'border-status-done/50 text-status-done' : 'text-muted-foreground'}>
          {machine.tunnel ? m.serve_tunnel_on() : m.serve_tunnel_off()}
        </Badge>
      )}
    </span>
  )
}

export function MachinesCard({ enabled = true }: { enabled?: boolean }) {
  const { data } = useMachines(enabled)
  const remove = useDeleteMachine()
  const machines = data?.machines ?? []
  const onRemove = (id: string) =>
    remove.mutate(id, {
      onSuccess: () => toast.success(m.serve_removed()),
      onError: (e) => toast.error(e instanceof ApiError ? e.message : m.common_error_generic()),
    })
  return (
    <section className="space-y-4 rounded-2xl border bg-card p-5" data-testid="machines-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">{m.serve_title()}</h2>
          <p className="text-sm text-muted-foreground">{m.serve_desc()}</p>
        </div>
        <Laptop className="size-5 shrink-0 text-muted-foreground" />
      </div>
      {machines.length ? (
        <ul className="divide-y rounded-xl border">
          {machines.map((mc) => (
            <li key={mc.id} className="flex items-center gap-3 p-3" data-testid="machine-row">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-medium">{mc.name}</span>
                  {mc.os ? <span className="text-xs text-muted-foreground">{mc.os}{mc.arch ? ` · ${mc.arch}` : ''}</span> : null}
                  <MachineStatus machine={mc} />
                </div>
                <p className="text-xs text-muted-foreground">
                  {mc.agents.length ? m.serve_agents({ list: mc.agents.map(agentName).join(', ') }) : m.serve_agents_none()}
                  {mc.lastSeenAt ? ` · ${m.serve_last_seen({ when: fmtDate(mc.lastSeenAt) })}` : ''}
                </p>
              </div>
              <Button loading={remove.isPending} size="icon" variant="ghost" className="size-8" aria-label={m.serve_remove()} onClick={() => onRemove(mc.id)} disabled={remove.isPending}>
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{m.serve_empty()}</p>
      )}
      <Button asChild size="sm" variant="secondary">
        <Link to="/serve">{m.serve_add()}</Link>
      </Button>
    </section>
  )
}
